import {useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {PageHeader} from '@common-web/components/page-header';
import {toast} from '@common-web/hooks/use-toast';
import {
  VipAddressFields,
  VipContactFields,
  VipIdentityFields,
  VipNameFields,
  VipTypeFields,
} from '@client/pages/vip/VipFormFields';
import {
  EMPTY_VIP_FORM_VALUES,
  buildVipCreateDetails,
  isVipFormValid,
  type VipFormValues,
} from '@client/pages/vip/vipForm';
import {Crown, Loader2, AlertCircle, ArrowLeft, CheckCircle2, Mail} from 'lucide-react';

type CreatedVipMember = {uniqueId: string; name: string; email: string};

export function CreateVipMember() {
  const [searchParams] = useSearchParams();
  // Internal users pass the in-scope client via the member-search filter; client
  // admins resolve to their own client server-side (clientId param is optional).
  const clientIdParam = searchParams.get('clientId') ?? undefined;

  const contextQuery = trpc.vip.context.useQuery({clientId: clientIdParam});
  const vipContext = contextQuery.data;

  const [form, setForm] = useState(EMPTY_VIP_FORM_VALUES);
  const [createdMember, setCreatedMember] = useState<CreatedVipMember | null>(null);

  const createVipMember = trpc.vip.create.useMutation({
    onSuccess: ({uniqueId}) => {
      const name = `${form.firstName} ${form.lastName}`.trim();
      toast({
        title: 'VIP member created',
        description: `${name} was added as a VIP (${uniqueId}).`,
      });
      setCreatedMember({uniqueId, name, email: form.email.trim()});
    },
    onError: createMutationErrorHandler('Error creating VIP member'),
  });

  const sendWelcomeEmail = trpc.vip.sendWelcomeEmail.useMutation({
    onSuccess: ({to}) => {
      toast({title: 'Welcome email sent', description: `A welcome message was sent to ${to}.`});
    },
    onError: createMutationErrorHandler('Error sending welcome email'),
  });

  const resetForNewMember = () => {
    setCreatedMember(null);
    setForm(EMPTY_VIP_FORM_VALUES);
  };

  const update = (patch: Partial<VipFormValues>) => setForm((prev) => ({...prev, ...patch}));

  const vipTypeOptions = vipContext?.vipTypeOptions ?? [];
  // Tenants without VIP-type options (e.g. Synrgy) don't collect type/brokerage.
  const usesVipType = vipTypeOptions.length > 0;

  const isValid = isVipFormValid(form, {mode: 'create', usesVipType});

  const handleSubmit = () => {
    if (!isValid || !vipContext) return;
    const memberDetails = buildVipCreateDetails(form, usesVipType);
    if (!memberDetails) return;
    createVipMember.mutate({clientId: vipContext.clientId, memberDetails});
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Add VIP Member'
          description='Create a VIP member for app access and a demo insurance card. VIPs are not enrolled in a real employer group.'
          icon={<Crown className='h-6 w-6' />}
        />
        <div className='px-8 py-6 max-w-4xl mx-auto w-full'>
          <Button
            asChild
            variant='ghost'
            className='mb-4 -ml-2 text-brand-gray hover:text-brand-navy'
          >
            <Link to='/member-search'>
              <ArrowLeft className='h-4 w-4 mr-2' />
              Back
            </Link>
          </Button>

          {contextQuery.isLoading ? (
            <div className='flex items-center justify-center py-16 text-brand-gray'>
              <Loader2 className='h-5 w-5 animate-spin' />
            </div>
          ) : !vipContext ? (
            <Alert>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle>VIP members aren't available here</AlertTitle>
              <AlertDescription>
                You don't have permission to add VIP members, or this client doesn't support them.
                {clientIdParam
                  ? ' Select a VIP-enabled client on the member search screen and try again.'
                  : ''}
              </AlertDescription>
            </Alert>
          ) : createdMember ? (
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='bg-gray-50 border-b'>
                <CardTitle className='flex items-center gap-2 text-lg font-semibold text-brand-navy'>
                  <CheckCircle2 className='h-5 w-5 text-green-600' />
                  VIP Member Created
                </CardTitle>
              </CardHeader>
              <CardContent className='p-6 space-y-6'>
                <div className='space-y-1'>
                  <p className='text-brand-navy font-medium'>{createdMember.name}</p>
                  <p className='text-sm text-brand-gray'>VIP ID: {createdMember.uniqueId}</p>
                  {createdMember.email && (
                    <p className='text-sm text-brand-gray'>{createdMember.email}</p>
                  )}
                </div>

                {!vipContext.welcomeEmailSupported ? (
                  <Alert>
                    <AlertCircle className='h-4 w-4' />
                    <AlertTitle>Welcome email not configured</AlertTitle>
                    <AlertDescription>
                      Welcome emails aren't set up for {vipContext.vbaShortClientName} VIP members
                      yet.
                    </AlertDescription>
                  </Alert>
                ) : createdMember.email ? (
                  <Alert>
                    <Mail className='h-4 w-4' />
                    <AlertTitle>Send the welcome message</AlertTitle>
                    <AlertDescription>
                      Email this VIP a welcome message with their group and broker support contact.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert>
                    <AlertCircle className='h-4 w-4' />
                    <AlertTitle>No email on file</AlertTitle>
                    <AlertDescription>
                      This VIP member was created without an email address, so a welcome message
                      can't be sent.
                    </AlertDescription>
                  </Alert>
                )}

                <div className='flex flex-col gap-3 sm:flex-row'>
                  <Button
                    className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold flex-1'
                    onClick={() =>
                      sendWelcomeEmail.mutate({
                        clientId: vipContext.clientId,
                        uniqueId: createdMember.uniqueId,
                      })
                    }
                    disabled={
                      !vipContext.welcomeEmailSupported ||
                      !createdMember.email ||
                      sendWelcomeEmail.isPending
                    }
                  >
                    {sendWelcomeEmail.isPending ? (
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                    ) : (
                      <Mail className='h-4 w-4 mr-2' />
                    )}
                    Send Welcome Email
                  </Button>
                  <Button variant='outline' className='flex-1' onClick={resetForNewMember}>
                    <Crown className='h-4 w-4 mr-2' />
                    Add Another VIP
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className='border-gray-200 shadow-sm'>
              <CardHeader className='bg-gray-50 border-b'>
                <CardTitle className='text-lg font-semibold text-brand-navy'>
                  VIP Member Details
                </CardTitle>
              </CardHeader>
              <CardContent className='p-6 space-y-6'>
                <VipNameFields variant='create' values={form} onChange={update} />
                <VipContactFields variant='create' values={form} onChange={update} />
                <VipIdentityFields variant='create' values={form} onChange={update} />
                <VipAddressFields variant='create' values={form} onChange={update} />

                {usesVipType && (
                  <VipTypeFields
                    variant='create'
                    values={form}
                    onChange={update}
                    vipTypeOptions={vipTypeOptions}
                  />
                )}

                <Alert>
                  <AlertCircle className='h-4 w-4' />
                  <AlertTitle>VIP members are synthetic</AlertTitle>
                  <AlertDescription>
                    This creates a VIP record in the {vipContext.vbaShortClientName} member feed for
                    app access and a demo insurance card only. It does not enroll the person in a
                    real employer group.
                  </AlertDescription>
                </Alert>

                <Button
                  className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold w-full'
                  onClick={handleSubmit}
                  disabled={!isValid || createVipMember.isPending}
                >
                  {createVipMember.isPending ? (
                    <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                  ) : (
                    <Crown className='h-4 w-4 mr-2' />
                  )}
                  Create VIP Member
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
